import React, {useState, useEffect, useCallback, useMemo, memo} from 'react';
import {Route, Routes, Navigate, Outlet, Link, useNavigate, useLocation} from 'react-router-dom';
import {useTheme} from "../components/ThemeContext/ThemeContext";
import {IoArrowBackCircleOutline} from "react-icons/io5";

import styles from './AppRouter.module.css';
import logo from '../assets/logo.svg';
import logoLight from '../assets/logo-light.svg';

import authApi from '../utils/auth.api';
import Login from "../components/Auth/Login";
import Register from "../components/Auth/Register";
import useApi from "../utils/useApi";
import MainNav from "../components/MainNav/MainNav";
import SuperAdminSettingsMenu from "../components/SuperAdminSettings/SuperAdminSettingsMenu/SuperAdminSettingsMenu";
import UserSettingsMenu from "../components/UserSettingsMenu/UserSettingsMenu";
import Notifications from "../components/Notifications/Notifications";
import ProtectedRoute from "../components/ProtectedRoute/ProtectedRoute";
import ManageUsers from "../components/SuperAdminSettings/ManageUsers/ManageUsers";
import {NavVisibilityProvider, useNavVisibility} from "../components/NavVisibilityContext/NavVisibilityContext";
import BackgroundMedia from "../components/BackgroundMedia/BackgroundMedia";
import ClientHome from "../components/Client/ClientHome/ClientHome";
import {initializeNavigationService} from '../utils/navigationService';
import UploadDocument from "../components/UploadDocument/UploadDocument";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";
import {ClientDataProvider, useClientData} from "../contexts/ClientDataContext";
import ClientDocuments from "../components/Client/ClientDocuments/ClientDocuments";
import FormList from "../components/FormList/FormList";
import TaxQuestionnaire from "../components/TaxQuestionnaire/TaxQuestionnaire";
import Profile from "../components/Profile/Profile";
import ClientForm from "../components/Admin/ClientForm/ClientForm";
import ClientDashboard from "../components/Admin/ClientDashboard/ClientDashboard";
import {Messages} from "../components/Messages/Messages";
import MessageShow from "../components/Messages/MessageShow";
import AdminClientDocuments from "../components/Admin/AdminClientDocuments/AdminClientDocuments";
import PortalRequestForm from "../components/Auth/PortalRequest/PortalRequest";
import PortalRequestDashboard from "../components/Admin/PortalRequestDashboard/PortalRequestDashboard";
import BackdropOverlay from "../components/BackdropOverlay/BackdropOverlay";
import PopoverProvider, {usePopover} from "../contexts/PopoverProvider";
import ClientAccount from "../components/Admin/ClientAccount/ClientAccount";
import ViewSentLetters from "../components/Admin/ViewSentLetters/ViewSentLetters";
import EmailTemplateSelector from "../components/Admin/EmailTemplateSelector/EmailTemplateSelector";
import SendLetter from "../components/Admin/SendLetter/SendLetter";
import Report from "../components/Admin/Report/Report";
import FormBuilder from "../components/Admin/FormBuilder/FormBuilder";
import {NavCountProvider} from "../contexts/NavCountProvider";
import Dashboard from "../components/Admin/Dashboard/Dashboard";
import ClientProgressDashboard from "../components/Admin/ClientProgressDashboard/ClientProgressDashboard";
import {TaxYearProvider, useTaxYear} from "../contexts/TaxYearContext";
import ForgotPassword from "../components/Auth/ForgotPassword";
import ResetPassword from "../components/Auth/ResetPassword";
import UserManagement from "../components/Admin/UserManagement/UserManagement";
import AddNewForm from "../components/Admin/AddNewForm/AddNewForm";
import DocumentTypeManagement from "../components/Admin/DocumentTypeManagement/DocumentTypeManagement";
import StepProtectedRoute from "../components/StepProtectedRoute/StepProtectedRoute";



const HomeRoute = memo(({ user, clientData, handleShowHeader, handleShowSettingsHeader, showTour, setShowTour }) => {
    if (!user) {
        return <Navigate to="/login" replace/>;
    }

    if (user.user_type === "admin") {
        return <Dashboard
            user={user}
            handleShowHeader={handleShowHeader}
            handleShowSettingsHeader={handleShowSettingsHeader}
        />;
    }

    return <ClientProgressDashboard
        user={user}
        clientData={clientData}
        handleShowHeader={handleShowHeader}
        handleShowSettingsHeader={handleShowSettingsHeader}
        showTour={showTour}
        setShowTour={setShowTour}
    />;
});


const AppRoutesContent = (props) => {
    const { clientData, setClientData } = useClientData();
    const [loginError, setLoginError] = useState(null);
    const [initializationState, setInitializationState] = useState({
                                                                       isLoading: true,
                                                                       error: null,
                                                                       user: null,
                                                                   });
    const [showHeader, setShowHeader] = useState(true);
    const [showSettingsHeader, setShowSettingsHeader] = useState(false);
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [locationInput, setLocationInput] = useState('');
    const [showTour, setShowTour] = useState(false);
    const { activePopoverId, closePopover } = usePopover();

    const { isDarkMode } = useTheme();
    const logoSrc = isDarkMode ? logoLight : logo;
    const backgroundMediaColor = isDarkMode ? "rgba(0,0,0,0.84)" : "rgba(255,255,255,0.87)";
    const navigate = useNavigate();
    const api = useApi();
    const location = useLocation();
    const { getClientHome } = useApi();
    const { setIsNavVisible } = useNavVisibility();
    const { updateTaxYear } = useTaxYear();
    // At the top of your component, memoize functions that don't need to change
    const memoizedGetClientHome = useCallback(getClientHome, []);
    const memoizedSetIsNavVisible = useCallback(setIsNavVisible, []);

    const hasInitialized = React.useRef(false);

    // Dynamic page width calculation (excludes scrollbar)
    useEffect(() => {
        const SIDEBAR_WIDTH = 310;
        const BREAKPOINT = 1100;

        const updatePageWidth = () => {
            const clientWidth = document.documentElement.clientWidth;
            if (clientWidth >= BREAKPOINT) {
                document.documentElement.style.setProperty(
                    '--page-width',
                    `${clientWidth - SIDEBAR_WIDTH}px`
                );
            } else {
                document.documentElement.style.removeProperty('--page-width');
            }
        };

        updatePageWidth();
        window.addEventListener('resize', updatePageWidth);
        return () => window.removeEventListener('resize', updatePageWidth);
    }, []);

    const clearApplicationState = useCallback(() => {
        setClientData(null);
        setInitializationState(prev => ({
            ...prev,
            user: null,
        }));
        hasInitialized.current = false;
    }, [setClientData]);

    useEffect(() => {
        let isMounted = true;

        const initializeApp = async () => {
            if (!isMounted) return;

            try {
                setInitializationState(prev => ({ ...prev, isLoading: true }));
                initializeNavigationService(navigate);
                memoizedSetIsNavVisible(true);

                const currentUser = await authApi.getCurrentUser();
                if (!isMounted) return;

                if (!currentUser) {
                    setInitializationState(prev => ({
                        ...prev,
                        isLoading: false,
                        user: null,
                    }));
                    return;
                }

                // Always fetch client data for the current user
                const newClientData = await memoizedGetClientHome();
                setClientData(newClientData);
                if (!isMounted) return;

                setClientData(newClientData);
                setInitializationState({
                                           isLoading: false,
                                           error: null,
                                           user: currentUser,
                                       });

                hasInitialized.current = true;
            }
            catch (error) {
                console.error('Initialization error:', error);
                if (isMounted) {
                    setInitializationState(prev => ({
                        ...prev,
                        isLoading: false,
                        error: error
                    }));
                }
            }
        };

        initializeApp();

        return () => {
            isMounted = false;
        };
    }, [navigate, memoizedGetClientHome, memoizedSetIsNavVisible, setClientData]);

    const handleShowHeader = useCallback((show) => {
        setShowHeader(show);
    }, []);

    const handleShowSettingsHeader = (newState) => {
        setShowSettingsHeader(newState);
    }

    const handleLogin = async (clientId, password, shouldNavigate) => {
        try {
            const userData = await authApi.login(clientId, password);
            localStorage.setItem('user', JSON.stringify(userData));

            // Check if this is the first login of the year
            const currentYear = new Date().getFullYear().toString();
            const lastLoginYear = localStorage.getItem('lastLoginYear');

            if (lastLoginYear !== currentYear) {
                // First login of the new year - reset to default tax year
                const defaultTaxYear = (new Date().getFullYear() - 1).toString();
                localStorage.removeItem('taxYear');
                updateTaxYear(defaultTaxYear);  // Also update React state
            }
            localStorage.setItem('lastLoginYear', currentYear);

            console.log(userData);
            // Always fetch fresh client data on login
            const newClientData = await getClientHome();
            setClientData(newClientData);

            setInitializationState(prev => ({
                ...prev,
                user: userData,
            }));

            if (shouldNavigate) {
                navigate('/');
            }

            return userData;
        }
        catch (error) {
            console.error('Login failed:', error);
            const errorMessage = error.data?.message || error.message || 'Login failed';
            setLoginError(errorMessage);
            return {
                success: false,
                status: error.status,
                message: errorMessage
            };
        }
    };

    const handleRegister = async (firstName, username, email, password) => {
        try {
            const response = await authApi.register(firstName, username, email, password);
            console.log(response);
            if (response?.data?.success) {
                const shouldNavigate = false;
                const userData = await handleLogin(email, password, shouldNavigate);
                console.log("Here is the user data", userData);

                if (userData) {
                    console.log("Checking onboarding status");
                    const onboardingStatus = await api.checkOnboardingStatus();

                    switch (onboardingStatus.status) {
                        case 'new':
                            navigate('/');
                            break;
                        case 'documents_uploaded':
                            navigate('/');
                            break;
                        default:
                            console.error('Unknown onboarding status:', onboardingStatus.status);
                            navigate('/');
                    }
                }
            } else {
                console.error('Registration failed:', response.message);
                throw new Error(response.message);
            }
        }
        catch (error) {
            console.error('Registration error:', error);
            throw new Error('An unexpected error occurred. Please try again.');
        }
    };

    const handleLogout = useCallback(() => {
        authApi.logout();
        clearApplicationState();
        navigate('/login');
    }, [clearApplicationState, navigate]);

    const tourSteps = [
        {
            element: '.main-nav',
            intro: 'This is your menu bar. Use this to navigate throughout the app.',
            position: 'top'
        },
        {
            element: '.home',
            intro: 'This is the main page, find all account details here.',
            position: 'top',
            customHighlight: 'menuButtonCustomHighlight',
        },
        {
            element: '.notifications',
            intro: 'Communicate directly with your tax preparer.',
            position: 'top',
            customHighlight: 'menuButtonCustomHighlightNotify',
        },
        {
            element: '.more',
            intro: 'Access all related settings for your profile here',
            position: 'top',
            customHighlight: 'menuButtonCustomHighlight',
        },
    ];

    const renderHeader = useCallback(() => {
        return (
            <div>
                <div className={styles.header}>
                    <div className={initializationState.user ? styles.upperHeader : styles.upperHeaderCentered}>
                        <Link to={"https://mckinneytaxservices.com"} className={styles.logoWrap}>
                            {/* <img src={logoSrc} alt="Logo" className={styles.logo}/> */}

                            <h2 className={styles.logoText}>McKinney Tax Services</h2>

                        </Link>
                    </div>
                </div>
            </div>
        );
    }, [initializationState.user, props.geoposition, logoSrc, props.locationLoading]);

    const renderSettingsHeader = () => {
        const getPageName = () => {
            const path = location.pathname;
            const pathParts = path.split('/');
            const lastPart = pathParts[pathParts.length - 1];
            return lastPart.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        };

        return (
            <div>
                <div className={styles.settingsHeader}>
                    <div className={styles.upperSettingsHeader}>
                        <div className={styles.backLink} style={{ width: "33%" }}>
                            <button onClick={() => navigate(-1)} className={styles.backButton}>
                                <IoArrowBackCircleOutline/>
                            </button>
                        </div>
                        <div className={styles.pageName} style={{ width: "51%", textAlign: 'center' }}>
                            {getPageName()}
                        </div>
                        <div className={styles.logoWrap} style={{ width: "33%" }}>
                            <Link to={"/"}>
                                <img src={logoSrc} alt="Logo" className={styles.logo} style={{ alignSelf: "end" }}/>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }



    // New component to protect public routes from authenticated users
    const PublicRoute = memo(({ user }) => {
        if (user) {
            return <Navigate to="/" replace/>;
        }
        return <Outlet/>;
    });

    // Memoize routes using initialization state
    const memoizedRoutes = useMemo(() => (
        <Routes>
            <Route element={<PublicRoute user={initializationState.user}/>}>
                <Route path="/login" element={
                    <Login
                        error={loginError}
                        onLogin={handleLogin}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        geoposition={props.geoposition}
                    />
                }/>
                <Route path="/register" element={
                    <Register
                        onRegister={handleRegister}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        geoposition={props.geoposition}
                    />
                }/>
                <Route path="/portal-request" element={
                    <PortalRequestForm
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        geoposition={props.geoposition}
                    />
                }/>
                <Route path="/forgot-password" element={<ForgotPassword handleShowHeader={handleShowHeader}
                                                                        handleShowSettingsHeader={handleShowSettingsHeader} />} />
                <Route path="/reset-password" element={<ResetPassword handleShowHeader={handleShowHeader}
                                                                      handleShowSettingsHeader={handleShowSettingsHeader} />} />


            </Route>

            <Route element={<ProtectedRoute user={initializationState.user}/>}>
                <Route path="/notifications" element={
                    <Notifications
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        user={initializationState.user}
                    />
                }/>

                <Route path="/user-settings" element={
                    <UserSettingsMenu
                        user={initializationState.user}
                        includeHello={true}
                        includeLogout={true}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        handleLogout={handleLogout}
                        setShowTour={setShowTour}
                    />
                }/>

                <Route exact path="/" element={
                    <HomeRoute
                        clientData={clientData}
                        user={initializationState.user}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        showTour={showTour}
                        setShowTour={setShowTour}
                    />
                }/>

                <Route path="/my-documents" element={
                    <ClientDocuments user={initializationState.user}
                                     handleShowHeader={handleShowHeader}
                                     handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/profile" element={
                    <Profile user={initializationState.user}
                             handleShowHeader={handleShowHeader}
                             handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/messages" element={
                    <Messages user={initializationState.user}
                              handleShowHeader={handleShowHeader}
                              handleShowSettingsHeader={handleShowSettingsHeader}
                    />
                }/>
                <Route path="/messages/:id" element={
                    <MessageShow user={initializationState.user}
                                 handleShowHeader={handleShowHeader}
                                 handleShowSettingsHeader={handleShowSettingsHeader}
                    />
                }/>

                <Route path="/worksheets" element={
                    <FormList user={initializationState.user}
                              handleShowHeader={handleShowHeader}
                              handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>
            </Route>

            {/* Step 1 Protection - Tax Questionnaire */}
            <Route element={<ProtectedRoute user={initializationState.user}/>}>
                <Route element={<StepProtectedRoute requiredStep={1} redirectTo="/profile" />}>
                    <Route path="/tax-questionnaire" element={
                        <TaxQuestionnaire user={initializationState.user}
                                          handleShowHeader={handleShowHeader}
                                          handleShowSettingsHeader={handleShowSettingsHeader}/>
                    }/>
                </Route>
            </Route>

            {/* Step 2 Protection - Upload Documents */}
            <Route element={<ProtectedRoute user={initializationState.user}/>}>
                <Route element={<StepProtectedRoute requiredStep={2} redirectTo="/profile" />}>
                    <Route path="/upload-documents" element={
                        <UploadDocument
                            user={initializationState.user}
                            handleShowHeader={handleShowHeader}
                            handleShowSettingsHeader={handleShowSettingsHeader}
                            showTour={showTour}
                            ClientData={clientData}
                            documentsNeeded={clientData?.documentsNeeded || []}
                            documentsCount={clientData?.documentsCount || 0}
                            finishedCount={clientData?.finishedCount || 0}
                            setShowTour={setShowTour}
                        />
                    }/>
                </Route>
            </Route>

            <Route element={<ProtectedRoute user={initializationState.user} requiredRole={['admin', 'superadmin']}/>}>
                <Route path="/clients" element={
                    <ClientDashboard
                        user={initializationState.user}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                    />
                } />

                <Route path="client-progress/:uid" element={
                    <ClientProgressDashboard
                        user={initializationState.user}
                        clientData={clientData}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        showTour={showTour}
                        setShowTour={setShowTour}
                    />
                } />


                <Route path="/admin/new-worksheet" element={
                    <AddNewForm user={initializationState.user}
                                handleShowHeader={handleShowHeader}
                                handleShowSettingsHeader={handleShowSettingsHeader} />
                } />

                <Route path="/tax-questionnaire-builder" element={
                    <FormBuilder
                        user={initializationState.user}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                    />
                }/>
                <Route path="/tax-questionnaire-builder/:id" element={
                    <FormBuilder
                        user={initializationState.user}
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                    />
                }/>

                <Route path="/reports" element={
                    <Report user={initializationState.user} handleShowHeader={handleShowHeader}
                            handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/portal-requests" element={
                    <PortalRequestDashboard handleShowHeader={handleShowHeader}
                                            handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/view-letters/:uid" element={
                    <ViewSentLetters user={initializationState.user}
                                     handleShowHeader={handleShowHeader}
                                     handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/send-letter/:uid" element={
                    <EmailTemplateSelector user={initializationState.user}
                                           handleShowHeader={handleShowHeader}
                                           handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/email-template/:template_id/:uid" element={
                    <SendLetter user={initializationState.user}
                                handleShowHeader={handleShowHeader}
                                handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                {/* Admin routes with step protection (admin bypass enabled) */}
                <Route element={<StepProtectedRoute requiredStep={1} redirectTo="/profile" adminBypass={true} />}>
                    <Route path="/client/questionnaire/:uid" element={
                        <TaxQuestionnaire user={initializationState.user}
                                          admin={initializationState.user}
                                          handleShowHeader={handleShowHeader}
                                          handleShowSettingsHeader={handleShowSettingsHeader}/>
                    }/>
                </Route>

                <Route path="/client/:id/edit" element={
                    <ClientForm
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        mode={'update'}
                    />
                }/>

                <Route element={<StepProtectedRoute requiredStep={2} redirectTo="/profile" adminBypass={true} />}>
                    <Route path="/client/:id/upload" element={
                        <UploadDocument
                            handleShowHeader={handleShowHeader}
                            handleShowSettingsHeader={handleShowSettingsHeader}
                            user={initializationState.user}
                        />
                    }/>
                </Route>

                <Route path="/new-client" element={
                    <ClientForm
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        mode={'create'}
                    />
                }/>

                <Route path="/clients/:id" element={
                    <ClientAccount user={initializationState.user}
                                   handleShowHeader={handleShowHeader}
                                   handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/documents/:user_id" element={
                    <AdminClientDocuments user={initializationState.user}
                                          handleShowHeader={handleShowHeader}
                                          handleShowSettingsHeader={handleShowSettingsHeader}/>
                }/>

                <Route path="/superadmin" element={
                    <SuperAdminSettingsMenu
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        user={initializationState.user}
                    />
                }/>
                <Route path="/admin/manage-users" element={
                    <UserManagement
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        user={initializationState.user}
                    />
                }/>
                <Route path="/admin/document-types" element={
                    <DocumentTypeManagement
                        handleShowHeader={handleShowHeader}
                        handleShowSettingsHeader={handleShowSettingsHeader}
                        user={initializationState.user}
                    />
                }/>

            </Route>

            <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
    ), [initializationState.user, clientData, handleLogin, props.geoposition]);

    // Now we can use conditional rendering for loading and error states
    if (initializationState.isLoading) {
        return (
            <LoadingScreen/>
        );
    }

    if (initializationState.error) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorMessage}>
                    An error occurred while loading the application.
                    Please try refreshing the page.
                </div>
            </div>
        );
    }

    return (
        <div>
            <BackgroundMedia
                geoposition={props.geoposition}
                customStyle={{ backgroundColor: backgroundMediaColor }}
            />

            <div data-open={!!activePopoverId}/>

            <BackdropOverlay
                isOpen={!!activePopoverId}
                onClose={closePopover}
            />


            {showHeader && renderHeader()}
            {showSettingsHeader && renderSettingsHeader()}

            {showSettingsHeader ? (
                <div className={`${styles.settingsContent} page-container`}>
                    {memoizedRoutes}
                </div>
            ) : memoizedRoutes}

            {(showTour && initializationState.user) && (
                <div className={`${styles.Navigation} ${styles.visible} main-nav-tour`}>
                    <MainNav
                        customClass={styles.mainNavTour}
                        user={initializationState.user}
                        onLogout={handleLogout}
                        showTour={showTour}
                        setShowTour={setShowTour}
                    />
                </div>
            )}

            {(!showTour && initializationState.user) && (
                <div className={`${styles.Navigation} main-nav-normal`}>
                    <MainNav
                        user={initializationState.user}
                        onLogout={handleLogout}
                        showTour={showTour}
                        setShowTour={setShowTour}

                    />
                </div>
            )}
        </div>
    );
};

const AppRoutes = (props) => {
    return (
        <NavVisibilityProvider>
            <NavCountProvider>
                <TaxYearProvider>
                    <ClientDataProvider>
                        <PopoverProvider>
                            <AppRoutesContent {...props} />
                        </PopoverProvider>
                    </ClientDataProvider>
                </TaxYearProvider>
            </NavCountProvider>
        </NavVisibilityProvider>
    );
};

export default React.memo(AppRoutes);
